import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useBodyScroll from '../hooks/useBodyScroll';
import {
  Section,
  Inner,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  Divider,
  Badge,
  fadeInUp,
} from '../styles/v2.styled';

const SUPPORT_TYPES = [
  {
    id: 'course',
    title: '수강 및 강의실',
    desc: '수강신청, 승인 상태, 내 강의실 접근 문제를 확인합니다.',
    action: '수강신청으로 이동',
    to: '/Enroll',
  },
  {
    id: 'account',
    title: '계정 및 로그인',
    desc: '로그인, 회원가입, 정보 수정 등 계정 문제를 처리합니다.',
    action: '마이페이지 확인',
    to: '/MyPage',
  },
  {
    id: 'resource',
    title: '자료 및 설치',
    desc: '설치 파일, 앱 다운로드, 학습 자료 접근을 안내합니다.',
    action: '자료실로 이동',
    to: '/Download',
  },
];

const FAQ_ITEMS = [
  {
    q: '수강신청 후 언제부터 강의를 볼 수 있나요?',
    a: '관리자 또는 담당 강사가 승인하면 내 강의실에서 수강 중인 과정을 확인할 수 있습니다.',
  },
  {
    q: '자료실 파일이 다운로드되지 않으면 어떻게 하나요?',
    a: '브라우저 다운로드 차단 여부를 먼저 확인하고, 계속 실패하면 고객센터로 파일명과 오류 화면을 보내주세요.',
  },
  {
    q: '강의 일정은 어디에서 확인하나요?',
    a: '상단 메뉴의 일정 페이지에서 전체 강의 일정을 확인할 수 있습니다.',
  },
  {
    q: '문의 답변은 어디에서 확인하나요?',
    a: '공지사항과 자유게시판을 우선 확인하고, 추가 문의는 이메일 또는 채팅방으로 남겨주세요.',
  },
];

const CHANNELS = [
  { title: '이메일', value: 'admin@antsnest.co.kr', desc: '오류 화면과 계정 정보를 함께 보내주세요.', href: 'mailto:admin@antsnest.co.kr' },
  { title: '전화', value: '070-9979-0814', desc: '긴급한 접속 문제를 빠르게 확인합니다.', href: 'tel:07099790814' },
  { title: '채팅방', value: '실시간 문의', desc: '운영 중인 채팅방에서 간단한 문의를 남길 수 있습니다.', to: '/join' },
];

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Hero = styled.section`
  padding: 72px 0 56px;
  background: linear-gradient(135deg, #062f35 0%, #075e69 55%, #0a8ea3 100%);
  color: #fff;
`;

const HeroInner = styled(Inner)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 44px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  animation: ${fadeInUp} 0.5s ease both;
`;

const Accent = styled.div`
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background: #67e8f9;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  margin: 0 0 14px;
  color: #fff;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 1.15;
`;

const HeroText = styled.p`
  max-width: 660px;
  margin: 0;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.75;
  font-size: 1.04rem;
`;

const StatusPanel = styled.div`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  padding: 24px;
  animation: ${fadeInUp} 0.5s 0.08s ease both;
`;

const StatusTitle = styled.div`
  font-weight: 900;
  font-size: 1.05rem;
  margin-bottom: 14px;
`;

const StatusList = styled.div`
  display: grid;
  gap: 10px;
`;

const StatusItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.9rem;
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SupportCard = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  padding: 24px;
  animation: ${fadeInUp} 0.45s ease both;
  animation-delay: ${({ $idx }) => $idx * 0.06}s;
`;

const CardLine = styled.div`
  width: 36px;
  height: 3px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.primary[500]};
  margin-bottom: 18px;
`;

const CardTitle = styled.h2`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-size: 1.18rem;
  font-weight: 900;
`;

const CardDesc = styled.p`
  min-height: 72px;
  margin: 0 0 18px;
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.6;
`;

const CardLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary[700]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
  text-decoration: none;
  font-weight: 900;
`;

const BoardBand = styled(Section)`
  background: ${({ theme }) => theme.colors.neutral[50]};
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const BoardLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 110px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 22px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  text-decoration: none;
`;

const BoardTitle = styled.div`
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-weight: 900;
  font-size: 1.08rem;
`;

const BoardDesc = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  font-size: 0.9rem;
`;

const Arrow = styled.span`
  color: ${({ theme }) => theme.colors.primary[700]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
  font-weight: 900;
  white-space: nowrap;
`;

const FaqList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FaqItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  margin-bottom: 10px;
  overflow: hidden;
`;

const FaqButton = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-weight: 900;
  cursor: pointer;
`;

const FaqAnswer = styled.div`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  padding: 0 20px 18px;
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.65;
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Channel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 22px;
`;

const ChannelTitle = styled.div`
  font-weight: 900;
  color: ${({ theme }) => theme.colors.neutral[900]};
  margin-bottom: 8px;
`;

const ChannelValue = styled.a`
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary[700]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
  text-decoration: none;
  font-weight: 900;
  margin-bottom: 8px;
`;

const ChannelText = styled.div`
  color: ${({ theme }) => theme.colors.neutral[500]};
  font-size: 0.88rem;
  line-height: 1.55;
`;

function HelpPage() {
  useBodyScroll();
  const [openFAQ, setOpenFAQ] = useState(0);

  return (
    <PageWrapper>
      <Hero>
        <HeroInner>
          <HeroCopy>
            <Accent />
            <HeroTitle>고객센터</HeroTitle>
            <HeroText>
              수강, 계정, 자료실, 강의실 접속 문제를 빠르게 찾을 수 있도록 정리했습니다.
              문의 전에 공지사항과 자주 묻는 질문을 먼저 확인하면 해결 시간이 줄어듭니다.
            </HeroText>
          </HeroCopy>
          <StatusPanel>
            <StatusTitle>지원 상태</StatusTitle>
            <StatusList>
              <StatusItem><span>이메일 접수</span><strong>24시간 이내</strong></StatusItem>
              <StatusItem><span>강의실 문의</span><strong>운영 중</strong></StatusItem>
              <StatusItem><span>자료실 점검</span><strong>상시 확인</strong></StatusItem>
            </StatusList>
          </StatusPanel>
        </HeroInner>
      </Hero>

      <Section $py="64px" $pyMobile="44px">
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>무엇을 도와드릴까요?</SectionTitle>
            <SectionSubtitle>문제 유형별로 가장 빠른 경로를 선택하세요.</SectionSubtitle>
          </SectionHeader>
          <SupportGrid>
            {SUPPORT_TYPES.map((item, index) => (
              <SupportCard key={item.id} $idx={index}>
                <CardLine />
                <CardTitle>{item.title}</CardTitle>
                <CardDesc>{item.desc}</CardDesc>
                <CardLink to={item.to}>{item.action}</CardLink>
              </SupportCard>
            ))}
          </SupportGrid>
        </Inner>
      </Section>

      <BoardBand $py="64px" $pyMobile="44px">
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>공지와 커뮤니티</SectionTitle>
            <SectionSubtitle>운영 공지와 수강생 문의를 한 번에 확인하세요.</SectionSubtitle>
          </SectionHeader>
          <BoardGrid>
            <BoardLink to="/NewsBoard/0">
              <div>
                <BoardTitle>공지사항</BoardTitle>
                <BoardDesc>점검, 업데이트, 강의 운영 안내</BoardDesc>
              </div>
              <Arrow>보기</Arrow>
            </BoardLink>
            <BoardLink to="/FreeBoard/0">
              <div>
                <BoardTitle>자유게시판</BoardTitle>
                <BoardDesc>학습 질문과 수강생 커뮤니티</BoardDesc>
              </div>
              <Arrow>보기</Arrow>
            </BoardLink>
          </BoardGrid>
        </Inner>
      </BoardBand>

      <Section $py="64px" $pyMobile="44px">
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>자주 묻는 질문</SectionTitle>
            <SectionSubtitle>반복 문의를 먼저 정리했습니다.</SectionSubtitle>
          </SectionHeader>
          <FaqList>
            {FAQ_ITEMS.map((item, index) => (
              <FaqItem key={item.q}>
                <FaqButton onClick={() => setOpenFAQ(openFAQ === index ? null : index)}>
                  <span>{item.q}</span>
                  <Badge $variant={openFAQ === index ? 'primary' : 'default'}>
                    {openFAQ === index ? '열림' : '보기'}
                  </Badge>
                </FaqButton>
                <FaqAnswer $open={openFAQ === index}>{item.a}</FaqAnswer>
              </FaqItem>
            ))}
          </FaqList>
        </Inner>
      </Section>

      <Section $py="64px" $pyMobile="44px">
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>문의 채널</SectionTitle>
            <SectionSubtitle>상황에 맞는 연락 수단을 선택하세요.</SectionSubtitle>
          </SectionHeader>
          <ChannelGrid>
            {CHANNELS.map((channel) => (
              <Channel key={channel.title}>
                <ChannelTitle>{channel.title}</ChannelTitle>
                {channel.to ? (
                  <ChannelValue as={Link} to={channel.to}>{channel.value}</ChannelValue>
                ) : (
                  <ChannelValue href={channel.href}>{channel.value}</ChannelValue>
                )}
                <ChannelText>{channel.desc}</ChannelText>
              </Channel>
            ))}
          </ChannelGrid>
        </Inner>
      </Section>
    </PageWrapper>
  );
}

export default HelpPage;
